import React, { useMemo, useCallback } from 'react';
import { KiteButton, KiteSelect, KiteIcon } from '@kite/react-kite';
import { TcoUnderlayProduct } from 'types/TcoProducts';
import './ApplySameUnderlayLocationCard.scss';
import NumberFormat from 'react-number-format';
import classNames from 'classnames';
import { tcoFormattedPrice } from 'utils/defaultsAndConstants/tcoConstants';

export interface UnderlayFormData {
  id?: string;
  underlayId: string;
  spectrumPrice: string;
  diyPrice: string;
  term?: string;
}

export interface IUnderlaySectionProps {
  underlayForms: UnderlayFormData[];
  formErrors: Record<string, string>;
  underlayProducts: TcoUnderlayProduct[];
  onAddUnderlay: () => void;
  onDeleteUnderlay: (index: number) => void;
  onUnderlayChange: (
    index: number,
    field: keyof UnderlayFormData,
    value: string
  ) => void;
  maxUnderlays: number;
  globalError?: boolean;
  isLoading?: boolean;
  term: string;
  noOfSites?: string;
}

const SameUnderlaySectionCard: React.FC<IUnderlaySectionProps> = ({
  underlayForms,
  formErrors,
  underlayProducts,
  onAddUnderlay,
  onDeleteUnderlay,
  onUnderlayChange,
  maxUnderlays,
  globalError,
  isLoading = false,
  term,
}) => {
  const isMaxUnderlaysReached = underlayForms.length >= maxUnderlays;

  const handleProductSelection = useCallback(
    (index: number, productId: string) => {
      const selectedProduct = underlayProducts.find(
        (product) => product.id === productId
      );

      if (!selectedProduct) {
        onUnderlayChange(index, 'underlayId', productId);
        return;
      }

      const selectedTerm = term || '1';

      if (!selectedProduct.prices || !Array.isArray(selectedProduct.prices)) {
        onUnderlayChange(index, 'underlayId', productId);
        return;
      }

      const priceInfo = selectedProduct.prices.find(
        (price) => price.term === selectedTerm
      );

      const fallbackPriceInfo = !priceInfo
        ? selectedProduct.prices.find((price) => price.term === '1')
        : null;
      if (priceInfo) {
        onUnderlayChange(index, 'underlayId', productId);
        onUnderlayChange(
          index,
          'spectrumPrice',
          priceInfo.spectrumPrice.toString()
        );
        onUnderlayChange(index, 'diyPrice', priceInfo.diyPrice.toString());
      } else if (fallbackPriceInfo) {
        onUnderlayChange(index, 'underlayId', productId);
        onUnderlayChange(
          index,
          'spectrumPrice',
          fallbackPriceInfo.spectrumPrice.toString()
        );
        onUnderlayChange(
          index,
          'diyPrice',
          fallbackPriceInfo.diyPrice.toString()
        );
      } else {
        onUnderlayChange(index, 'underlayId', productId);
      }
    },
    [underlayProducts, onUnderlayChange, term]
  );

  const sortedUnderlayProducts = useMemo(() => {
    return [...underlayProducts].sort((a, b) => a.order - b.order);
  }, [underlayProducts]);

  return (
    <>
      {underlayForms.map((form, index) => {
        const toBeDisabled =
          (form.underlayId &&
            !underlayProducts.find((p) => p.id === form.underlayId)
              ?.published) ||
          false;
        const productsList = toBeDisabled
          ? sortedUnderlayProducts
          : sortedUnderlayProducts.filter((p) => p.published);

        return (
          <div key={index} className="sameunderlay-section">
            <div className="sameunderlay-section__header">
              <button
                className="sameunderlay-section__delete-button"
                onClick={() => onDeleteUnderlay(index)}
                aria-label={`Delete Underlay ${index + 1}`}
                type="button"
              >
                <KiteIcon
                  name="minus-circle"
                  className="underlay-section__delete-icon"
                />
              </button>
            </div>

            <div className="sameunderlay-section__site-underlay">
              <label htmlFor={`siteUnderlay-${index}`} className="kite-label">
                {`Underlay ${index + 1}`}
                {toBeDisabled && <KiteIcon name="lock"></KiteIcon>}
              </label>
              <KiteSelect
                id={`siteUnderlay-${index}`}
                name={`Underlay ${index + 1}`}
                value={form.underlayId}
                onChange={(e) => handleProductSelection(index, e.target.value)}
                placeholder="Select One"
                maxWidth="none"
                errorMessage={formErrors[`underlayId_${index}`]}
                disabled={toBeDisabled}
              >
                {isLoading && (
                  <option value="" disabled>
                    Loading underlay products...
                  </option>
                )}
                {!isLoading && sortedUnderlayProducts.length === 0 && (
                  <option value="" disabled>
                    No underlay products available
                  </option>
                )}
                {productsList.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </KiteSelect>
            </div>

            <div className="sameunderlay-section__price-row">
              <div className="price-column">
                <h4 className="price-column__title">Spectrum</h4>
                <div className="price-input-container">
                  <label
                    htmlFor={`spectrumPrice-${index}`}
                    className="kite-label"
                  >
                    Price
                  </label>
                  <NumberFormat
                    id={`spectrumPrice-${index}`}
                    name="spectrumPrice"
                    value={tcoFormattedPrice(form.spectrumPrice)}
                    thousandSeparator={true}
                    prefix={'$'}
                    decimalScale={2}
                    allowNegative={false}
                    isNumericString
                    onValueChange={(values) => {
                      onUnderlayChange(index, 'spectrumPrice', values.value);
                    }}
                    className={classNames({
                      'kite-form-control': true,
                      'kite-input__input': true,
                      'kite-form-control-danger':
                        formErrors[`spectrumPrice_${index}`],
                    })}
                    placeholder="Enter Price"
                    disabled={toBeDisabled}
                  />
                  {formErrors[`spectrumPrice_${index}`] && (
                    <p className="kite-form-control-feedback">
                      {formErrors[`spectrumPrice_${index}`]}
                    </p>
                  )}
                </div>
              </div>
              <div className="price-column">
                <h4 className="price-column__title">DIY</h4>
                <div className="price-input-container">
                  <label htmlFor={`diyPrice-${index}`} className="kite-label">
                    Price
                  </label>
                  <NumberFormat
                    id={`diyPrice-${index}`}
                    name="diyPrice"
                    value={tcoFormattedPrice(form.diyPrice)}
                    thousandSeparator={true}
                    prefix={'$'}
                    decimalScale={2}
                    allowNegative={false}
                    isNumericString
                    onValueChange={(values) => {
                      onUnderlayChange(index, 'diyPrice', values.value);
                    }}
                    className={classNames({
                      'kite-form-control': true,
                      'kite-input__input': true,
                      'kite-form-control-danger':
                        formErrors[`diyPrice_${index}`],
                    })}
                    placeholder="Enter Price"
                    disabled={toBeDisabled}
                  />
                  {formErrors[`diyPrice_${index}`] && (
                    <p className="kite-form-control-feedback">
                      {formErrors[`diyPrice_${index}`]}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}

      <div>
        <KiteButton
          type="outline"
          className="add_underlay_button"
          onClick={onAddUnderlay}
          leftIcon="plus-circle"
          disabled={isMaxUnderlaysReached || isLoading}
        >
          {isLoading
            ? 'Loading...'
            : `Add Underlay ${isMaxUnderlaysReached ? '(Maximum of 2)' : ''}`}
        </KiteButton>
      </div>
    </>
  );
};

export default SameUnderlaySectionCard;
